<template>
  <SalesPlayDrawerCard>
    <div class="drawer-section">
      <div class="drawer-sectionLabel">TIME WINDOW</div>
      <div class="windowToggle">
        <button
          v-for="m in modes"
          :key="m.key"
          class="toggleBtn"
          :class="{ active: windowMode === m.key }"
          type="button"
          @click="setMode(m.key)"
        >{{ m.label }}</button>
      </div>
    </div>

    <div v-if="windowMode === 'last'" class="drawer-section">
      <div class="fieldRow">
        <label class="fieldLabel">Look back</label>
        <input
          type="number"
          class="fieldInput numInput"
          v-model.number="lastValue"
          min="1"
          @blur="applyWindow"
        />
        <select class="fieldInput unitSelect" v-model="lastUnit" @change="applyWindow">
          <option value="days">days</option>
          <option value="weeks">weeks</option>
          <option value="months">months</option>
        </select>
      </div>
    </div>

    <div v-if="windowMode === 'since'" class="drawer-section">
      <div class="fieldRow">
        <label class="fieldLabel">Since</label>
        <input type="date" class="fieldInput dateInput" v-model="sinceDate" @blur="applyWindow" />
      </div>
    </div>

    <div v-if="windowMode === 'between'" class="drawer-section">
      <div class="fieldRow">
        <label class="fieldLabel">From</label>
        <input type="date" class="fieldInput dateInput" v-model="betweenFrom" @blur="applyWindow" />
      </div>
      <div class="fieldRow">
        <label class="fieldLabel">To</label>
        <input type="date" class="fieldInput dateInput" v-model="betweenTo" @blur="applyWindow" />
      </div>
    </div>

    <!-- Preview -->
    <div class="drawer-section">
      <button class="btn secondary" type="button" :disabled="!isValid || previewing" @click="onPreview">
        {{ previewing ? 'Loading...' : 'Preview' }}
      </button>
    </div>

    <div v-if="previewError" class="drawer-section">
      <div class="drawer-errorText">{{ previewError }}</div>
    </div>

    <div v-if="previewTotal !== null" class="drawer-section">
      <div class="drawer-sectionLabel">
        PREVIEW
        <span class="drawer-previewMeta">{{ previewTotal.toLocaleString() }} event{{ previewTotal !== 1 ? 's' : '' }}</span>
      </div>
      <div v-if="previewRecords.length > 0" class="drawer-previewTable">
        <table>
          <thead>
            <tr>
              <th v-for="col in previewColumns" :key="col">{{ col }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in previewRecords" :key="i">
              <td v-for="col in previewColumns" :key="col">{{ formatCell(row[col]) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="emptyText">No events found for this window.</div>
    </div>

  </SalesPlayDrawerCard>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import SalesPlayDrawerCard from './SalesPlayDrawerCard.vue'
import { apiFetch } from '../../../services/api'
import { useSalesPlayDraft } from '../../composables/useSalesPlayDraft'

const props = defineProps<{
  playId: string
  pipelineId: string | null
  nodeIndex: number
  signalTypes?: string[]
}>()

const { nodes, updateNodeConfig } = useSalesPlayDraft()

const modes = [
  { key: 'last' as const, label: 'Look back' },
  { key: 'since' as const, label: 'Since date' },
  { key: 'between' as const, label: 'Date range' },
]

type WindowMode = 'last' | 'since' | 'between'

const currentConfig = () => nodes.value[props.nodeIndex]?.config || {}

// ── Local state ──────────────────────────────────────────────────

const windowMode = ref<WindowMode>('last')
const lastValue = ref(14)
const lastUnit = ref<'days' | 'weeks' | 'months'>('days')
const sinceDate = ref('')
const betweenFrom = ref('')
const betweenTo = ref('')

function parseConfigWindow(config: Record<string, any>) {
  const w = config.window
  if (!w || typeof w !== 'object') {
    windowMode.value = 'last'
    lastValue.value = 14
    lastUnit.value = 'days'
    sinceDate.value = ''
    betweenFrom.value = ''
    betweenTo.value = ''
    return
  }
  if (w.last) {
    windowMode.value = 'last'
    const keys = Object.keys(w.last)
    const unit = keys.find((k) => ['days', 'weeks', 'months'].includes(k)) || 'days'
    lastUnit.value = unit as 'days' | 'weeks' | 'months'
    lastValue.value = w.last[unit] ?? 14
  } else if (w.since) {
    windowMode.value = 'since'
    sinceDate.value = String(w.since).slice(0, 10)
  } else if (w.between && Array.isArray(w.between)) {
    windowMode.value = 'between'
    betweenFrom.value = String(w.between[0] || '').slice(0, 10)
    betweenTo.value = String(w.between[1] || '').slice(0, 10)
  }
}

function resetLocal() {
  parseConfigWindow(currentConfig())
  previewRecords.value = []
  previewTotal.value = null
  previewError.value = ''
}

watch(() => nodes.value[props.nodeIndex]?.config, () => {
  resetLocal()
}, { deep: true })

// Initialize on mount
parseConfigWindow(currentConfig())

// ── Validation ───────────────────────────────────────────────────

const isValid = computed(() => {
  if (windowMode.value === 'last') return lastValue.value > 0
  if (windowMode.value === 'since') return sinceDate.value.length > 0
  if (windowMode.value === 'between') return betweenFrom.value.length > 0 && betweenTo.value.length > 0 && betweenFrom.value <= betweenTo.value
  return false
})

// ── Preview ──────────────────────────────────────────────────────

const previewing = ref(false)
const previewError = ref('')
const previewRecords = ref<any[]>([])
const previewTotal = ref<number | null>(null)

const previewColumns = computed(() => {
  if (previewRecords.value.length === 0) return []
  return Object.keys(previewRecords.value[0])
})

async function onPreview() {
  if (!isValid.value) return
  previewing.value = true
  previewError.value = ''
  previewRecords.value = []
  previewTotal.value = null

  try {
    const body: Record<string, any> = {
      window: buildWindow(),
      limit: 10,
    }
    if (props.signalTypes && props.signalTypes.length > 0) {
      body.signal_types = props.signalTypes
    }
    const resp: any = await apiFetch('/integrations/warmly/events-preview', {
      method: 'POST',
      body,
    })
    previewRecords.value = resp.records || []
    previewTotal.value = resp.total_count ?? 0
  } catch (e: any) {
    previewError.value = e?.message || 'Preview failed'
  } finally {
    previewing.value = false
  }
}

function formatCell(value: any): string {
  if (value === null || value === undefined) return '\u2014'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// ── Actions ──────────────────────────────────────────────────────

function setMode(mode: WindowMode) {
  windowMode.value = mode
  applyWindow()
}

function buildWindow(): Record<string, any> {
  if (windowMode.value === 'last') return { last: { [lastUnit.value]: lastValue.value } }
  if (windowMode.value === 'since') return { since: sinceDate.value }
  return { between: [betweenFrom.value, betweenTo.value] }
}

function applyWindow() {
  // Only a fully-formed window is ever valid to send — a half-entered
  // date range, for instance, stays local-only until it's complete.
  if (!isValid.value) return
  updateNodeConfig(props.nodeIndex, { window: buildWindow() })
}
</script>

<style scoped>
.windowToggle {
  display: flex;
  gap: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}
.toggleBtn {
  flex: 1;
  padding: var(--space-1) var(--space-2);
  font-size: var(--fs-xs);
  font-family: var(--font-sans);
  background: var(--color-bg-app);
  color: var(--color-text-secondary);
  border: none;
  border-right: 1px solid var(--color-border);
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}
.toggleBtn:last-child {
  border-right: none;
}
.toggleBtn.active {
  background: var(--color-accent);
  color: #fff;
}

.fieldRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}
.fieldLabel {
  font-size: var(--fs-sm);
  color: var(--color-text-secondary);
  min-width: 60px;
}
.fieldInput {
  padding: var(--space-1) var(--space-2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: var(--fs-sm);
  font-family: var(--font-sans);
  background: var(--color-bg-app);
  color: var(--color-text-primary);
}
.fieldInput:focus {
  outline: none;
  border-color: var(--color-accent);
}
.numInput {
  width: 72px;
}
.unitSelect {
  width: 100px;
}
.dateInput {
  flex: 1;
}

.emptyText {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  font-style: italic;
}


</style>
